<template>
  <fieldset class="assessment-blood-pressure-input" :disabled="disabled">
    <legend class="assessment-blood-pressure-input__label">{{ label }}</legend>
    <div class="assessment-blood-pressure-input__fields">
      <input
        :id="`${inputId}-systolic`"
        :value="systolicValue"
        type="number"
        inputmode="numeric"
        class="assessment-blood-pressure-input__field"
        :class="{
          'assessment-blood-pressure-input__field--warning': systolicWarning && !systolicInvalid,
        }"
        data-testid="assessment-blood-pressure-systolic"
        :aria-label="systolicLabel"
        :aria-invalid="String(systolicInvalid)"
        :min="ranges && ranges.systolic.min"
        :max="ranges && ranges.systolic.max"
        step="1"
        @input="onPartInput('systolic', $event)"
        @blur="onPartBlur"
      >
      <span class="assessment-blood-pressure-input__separator" aria-hidden="true">/</span>
      <input
        :id="`${inputId}-diastolic`"
        :value="diastolicValue"
        type="number"
        inputmode="numeric"
        class="assessment-blood-pressure-input__field"
        :class="{
          'assessment-blood-pressure-input__field--warning': diastolicWarning && !diastolicInvalid,
        }"
        data-testid="assessment-blood-pressure-diastolic"
        :aria-label="diastolicLabel"
        :aria-invalid="String(diastolicInvalid)"
        :min="ranges && ranges.diastolic.min"
        :max="ranges && ranges.diastolic.max"
        step="1"
        @input="onPartInput('diastolic', $event)"
        @blur="onPartBlur"
      >
      <span class="assessment-blood-pressure-input__unit">{{ displayUnit }}</span>
    </div>
  </fieldset>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue'
import './styles/BloodPressureInput.css'

interface ScalarRange {
  min: number
  max: number
  warningMin?: number
  warningMax?: number
}

interface BloodPressureRanges {
  unit: 'mmHg'
  systolic: ScalarRange
  diastolic: ScalarRange
}

interface BloodPressureParts {
  systolic: string
  diastolic: string
}

function partsFromValue(value: string | null): BloodPressureParts {
  if (!value) return { systolic: '', diastolic: '' }
  const match = value.match(/^\s*(\d*)\s*\/\s*(\d*)\s*$/)
  return match
    ? { systolic: match[1], diastolic: match[2] }
    : { systolic: '', diastolic: '' }
}

export default defineComponent({
  name: 'AssessmentBloodPressureInput',
  props: {
    value: { type: String as PropType<string | null>, default: null },
    inputId: { type: String, required: true },
    label: { type: String, required: true },
    ranges: { type: Object as PropType<BloodPressureRanges | null>, default: null },
    unit: { type: String, default: 'mmHg' },
    systolicLabel: { type: String, default: 'Systolic blood pressure' },
    diastolicLabel: { type: String, default: 'Diastolic blood pressure' },
    systolicInvalid: { type: Boolean, default: false },
    diastolicInvalid: { type: Boolean, default: false },
    systolicWarning: { type: Boolean, default: false },
    diastolicWarning: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  emits: ['input', 'parts', 'entry-complete'],
  data() {
    const parts = partsFromValue(this.value)
    return {
      systolicValue: parts.systolic,
      diastolicValue: parts.diastolic,
    }
  },
  computed: {
    displayUnit(): string {
      return this.ranges?.unit ?? this.unit
    },
  },
  watch: {
    value(nextValue: string | null): void {
      const parts = partsFromValue(nextValue)
      this.systolicValue = parts.systolic
      this.diastolicValue = parts.diastolic
    },
  },
  methods: {
    onPartInput(part: keyof BloodPressureParts, event: Event): void {
      const value = (event.target as HTMLInputElement).value
      this[`${part}Value`] = value
      const parts = {
        systolic: this.systolicValue,
        diastolic: this.diastolicValue,
      }
      this.$emit('parts', parts)
      this.$emit('input', parts.systolic || parts.diastolic
        ? `${parts.systolic}/${parts.diastolic}`
        : '')
    },
    onPartBlur(): void {
      if (this.systolicValue && this.diastolicValue) this.$emit('entry-complete')
    },
  },
})
</script>
